import { createInput, createSelect, MountTarget, newDialog, readConfig } from './utils/elements';

type Sign = 'add' | 'subtract' | 'multiply' | 'divide';

export const REKENTABEL_EDIT_SELECTOR = '.cds-lrn-rekentabel';

interface RekentabelConfig {
  arrayRows: string;
  arrayColumns: string;
  sign: Sign;
}

export function rekentabelDialog(
  insertHtml: (html: string) => void,
  mountTarget?: MountTarget,
  existingEl?: HTMLElement
) {
  const size = 80;

  const dialog = newDialog('Rekentabel maken', () => insertHtml(''), mountTarget);

  const genericContainer = document.createElement('div');

  const arrayContainer = document.createElement('div');
  const rowArrayInput = createInput(
    'arrayRows',
    'Waarden rijen',
    'text',
    'Lijst van waarden gescheiden door punt-komma bv. 1;2;3'
  );
  const columnArrayInput = createInput(
    'arrayColumns',
    'Waarden kolommen',
    'text',
    'Lijst van waarden gescheiden door punt-komma bv. 1;2;3'
  );

  arrayContainer.append(rowArrayInput.field, columnArrayInput.field);

  const signSelect = createSelect('sign', 'Bewerking', [
    { label: 'plus', value: 'add' },
    { label: 'min', value: 'subtract' },
    { label: 'keer', value: 'multiply' },
    { label: 'gedeeld door', value: 'divide' },
  ]);

  if (existingEl) {
    const cfg = readConfig<RekentabelConfig>(existingEl, { arrayRows: '', arrayColumns: '', sign: 'add' });
    if (cfg.arrayRows) rowArrayInput.setValue(cfg.arrayRows);
    if (cfg.arrayColumns) columnArrayInput.setValue(cfg.arrayColumns);
    if (cfg.sign) signSelect.setValue(cfg.sign);
  }

  dialog.querySelector('#cds-dialog-content').append(genericContainer, arrayContainer, signSelect.field);

  dialog.querySelector('#cds-dialog-content').append(signSelect.field);

  dialog.addEventListener('onSubmit', () => {
    const config: RekentabelConfig = {
      arrayRows: rowArrayInput.value(),
      arrayColumns: columnArrayInput.value(),
      sign: signSelect.value() as Sign,
    };
    const rows = config.arrayRows.split(';');
    const columns = config.arrayColumns.split(';');

    const table = makeTable(rows, columns, config.sign, size, config);
    insertHtml(table);
  });
}


function makeTable(rows: string[], columns: string[], sign: Sign, size: number, config: RekentabelConfig) {
  const table = document.createElement('table');
  table.classList.add('cds-lrn-rekentabel', 'cds-rekentabel', 'cds-lrn-editor-table', 'table', 'table-bordered', 'lrn_width_auto');
  table.setAttribute('data-cb-config', JSON.stringify(config));
  for (let i = 0; i < rows.length + 1; i++) {
    const row = document.createElement('tr');
    for (let j = 0; j < columns.length + 1; j++) {
      if (i === 0 && j === 0) {
        const cell = document.createElement('td');
        cell.classList.add('relative', 'primary-container');
        cell.style.height = size + 'px';
        cell.style.width = size + 'px';
        cell.style.verticalAlign = 'bottom';
        cell.style.textAlign = 'right';

        const svg = makeSvg(sign, size);
        svg.classList.add('absolute', 'bottom-0', 'right-0', '-transform-full');
        cell.append(svg);
        row.append(cell);
      } else {
        const cell = document.createElement('td');
        if (i === 0) {
          cell.style.verticalAlign = 'bottom';
          cell.style.textAlign = 'center';

          const span = document.createElement('span');
          span.innerHTML = columns[j - 1];
          span.classList.add('title-medium');

          cell.append(span);
        } else if (j === 0) {
          cell.style.textAlign = 'right';

          const span = document.createElement('span');
          span.classList.add('title-medium');
          span.innerHTML = rows[i - 1];
          cell.append(span);
        } else {
          cell.style.textAlign = 'center';
          cell.innerHTML = 'placeholder';
        }
        row.append(cell);
      }
    }
    table.append(row);
  }
  return table.outerHTML;
}

function makeSvg(sign: Sign, size) {
  const svg = document.createElement('svg');
  svg.setAttribute('width', size);
  svg.setAttribute('height', size);
  svg.setAttribute('preserveAspectRatio', 'none');
  svg.setAttribute('viewBox', '0 0 40 40');
  svg.style.overflow = 'visible';

  const arrowGroup = document.createElement('g');

  const line = document.createElement('path');
  line.setAttribute('d', 'M34.09,20.89c-8.16,2.52-14.09,10.12-14.09,19.11');
  line.classList.add('stroke-stroke');
  line.style.fill = 'none';
  line.style.strokeWidth = '1px';

  const arrow = document.createElement('polygon');
  arrow.setAttribute('points', '30.71 17.89 32.91 21.26 32 25.18 40 20 30.71 17.89');
  arrow.style.strokeWidth = '0px';
  arrow.classList.add('fill-stroke');

  arrowGroup.append(line);
  arrowGroup.append(arrow);

  svg.append(arrowGroup);

  let signEl: HTMLElement;
  switch (sign) {
    case 'add':
      signEl = document.createElement('polygon');
      signEl.setAttribute(
        'points',
        '24.01 19.64 20.82 19.64 20.82 16.44 19.14 16.44 19.14 19.64 15.99 19.64 15.99 21.08 19.14 21.08 19.14 24.17 20.82 24.17 20.82 21.08 24.01 21.08 24.01 19.64'
      );
      break;
    case 'subtract':
      signEl = document.createElement('path');
      signEl.setAttribute('d', 'M15.99,21.08v-1.44h8.02v1.44h-8.02Z');
      break;
    case 'multiply':
      signEl = document.createElement('polygon');
      signEl.setAttribute(
        'points',
        '23.3 22.67 21.05 20.42 23.31 18.15 22.13 16.97 19.86 19.23 17.64 17 16.62 18.02 18.85 20.25 16.66 22.43 17.85 23.62 20.03 21.44 22.29 23.69 23.3 22.67'
      );
      break;
    case 'divide':
      signEl = document.createElement('path');
      signEl.setAttribute(
        'd',
        'M20,18.04c-.35,0-.62-.1-.82-.31-.19-.21-.29-.5-.29-.89,0-.35.1-.64.3-.86.2-.22.47-.34.8-.34.35,0,.62.1.82.31.19.21.29.5.29.89,0,.35-.1.64-.3.86-.2.22-.47.34-.8.34ZM20,24.36c-.35,0-.62-.1-.82-.31-.19-.21-.29-.5-.29-.89,0-.35.1-.64.3-.86.2-.22.47-.34.8-.34.35,0,.62.1.82.31.19.21.29.5.29.89,0,.35-.1.64-.3.86-.2.22-.47.34-.8.34Z'
      );
      break;
  }
  signEl.classList.add('fill-stroke');
  signEl.style.strokeWidth = '0px';
  svg.append(signEl);

  return svg;

  // <svg viewBox="0 0 40 40">
  //   <path
  //     class="stroke-stroke"
  //     d="M34.09,20.89c-8.16,2.52-14.09,10.12-14.09,19.11"
  //     style="fill: none; stroke-width: 2px"
  //   />

  //   <polygon
  //     class="fill-stroke"
  //     points="24.01 19.64 20.82 19.64 20.82 16.44 19.14 16.44 19.14 19.64 15.99 19.64 15.99 21.08 19.14 21.08 19.14 24.17 20.82 24.17 20.82 21.08 24.01 21.08 24.01 19.64"
  //     style="stroke-width: 0px"
  //   />
  //   <polygon
  //     class="fill-stroke"
  //     points="23.3 22.67 21.05 20.42 23.31 18.15 22.13 16.97 19.86 19.23 17.64 17 16.62 18.02 18.85 20.25 16.66 22.43 17.85 23.62 20.03 21.44 22.29 23.69 23.3 22.67"
  //     style="stroke-width: 0px"
  //   />
  //   <path
  //     class="fill-stroke"
  //     d="M15.99,21.08v-1.44h8.02v1.44h-8.02Z"
  //     style="stroke-width: 0px"
  //   />
  //   <path
  //     class="fill-stroke"
  //     d="M20,18.04c-.35,0-.62-.1-.82-.31-.19-.21-.29-.5-.29-.89,0-.35.1-.64.3-.86.2-.22.47-.34.8-.34.35,0,.62.1.82.31.19.21.29.5.29.89,0,.35-.1.64-.3.86-.2.22-.47.34-.8.34ZM20,24.36c-.35,0-.62-.1-.82-.31-.19-.21-.29-.5-.29-.89,0-.35.1-.64.3-.86.2-.22.47-.34.8-.34.35,0,.62.1.82.31.19.21.29.5.29.89,0,.35-.1.64-.3.86-.2.22-.47.34-.8.34Z"
  //     style="stroke-width: 0px"
  //   />
  // </svg>
}
